import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {RGBA} from '@Utils/color.util';
import {rgbaToRGBAString} from '@Utils/color.util';
import {hexToRgba} from '@Utils/color.util';
import {lightenDarkenColor} from '@Utils/color.util';
import type * as Fabric from '@postermywall/fabricjs-2';
import {Gradient} from '@postermywall/fabricjs-2';

export enum FillTypes {
  SOLID = 0,
  LINEAR_GRADIENT = 1,
  RADIAL_GRADIENT = 2,
  NONE = 3,
}

export interface FillObject {
  fillType: FillTypes;
  fillColor: RGBA[];
}

export const DEFAULT_FILL_COLOR: RGBA = [0, 0, 0, 1];

export interface FillOpts {
  getRadialGradientOpts?(fillWidth: number, fillHeight: number, gradientFillColors: RGBA[]): Fabric.GradientOptions<'radial'>;

  getLinearGradientOpts?(fillHeight: number, gradientFillColors: RGBA[]): Fabric.GradientOptions<'linear'>;
}

export class Fill extends CommonMethods {
  public fillType: FillTypes = FillTypes.SOLID;
  public fillColor: RGBA[] = [DEFAULT_FILL_COLOR];

  public fillOpts: FillOpts = {};

  public constructor(opts: FillOpts = {}) {
    super();
    this.fillOpts = opts;
  }

  public getFill(fillWidth: number, fillHeight: number): Fabric.TFiller | string {
    if (this.hasLinearGradientFill()) {
      const gradientOpts = this.getLinearGradientOpts(fillHeight);
      return new Gradient(gradientOpts);
    }
    if (this.hasRadialGradientFill()) {
      const gradientOpts = this.getRadialGradientOpts(fillWidth, fillHeight);
      return new Gradient(gradientOpts);
    }

    if (!this.hasFill()) {
      return rgbaToRGBAString([this.fillColor[0][0], this.fillColor[0][1], this.fillColor[0][2], 0]);
    }
    return rgbaToRGBAString(this.fillColor[0]);
  }

  public hasFill(): boolean {
    return this.fillType !== FillTypes.NONE;
  }

  public hasGradientFill(): boolean {
    return this.hasLinearGradientFill() || this.hasRadialGradientFill();
  }

  public getFillColor(): RGBA[] {
    return this.fillColor;
  }

  public hasLinearGradientFill(): boolean {
    return this.fillType === FillTypes.LINEAR_GRADIENT;
  }

  public hasRadialGradientFill(): boolean {
    return this.fillType === FillTypes.RADIAL_GRADIENT;
  }

  public hasSolidFill(): boolean {
    return this.fillType === FillTypes.SOLID;
  }

  public toObject(): FillObject {
    return {
      fillType: this.fillType,
      fillColor: this.fillColor,
    };
  }

  public getColorForNewType(newType: FillTypes): RGBA[] {
    if (newType === FillTypes.NONE) {
      return this.fillColor;
    }

    if (this.hasGradientFill() && (newType === FillTypes.LINEAR_GRADIENT || newType === FillTypes.RADIAL_GRADIENT)) {
      return this.fillColor;
    }

    if (newType === FillTypes.RADIAL_GRADIENT || newType === FillTypes.LINEAR_GRADIENT) {
      return this.getGradientColorsFromPrimaryColor(this.fillColor[0]);
    }

    return [this.fillColor[0]];
  }

  public getGradientColorsFromPrimaryColor(primaryColor: RGBA): RGBA[] {
    return [primaryColor, hexToRgba(lightenDarkenColor(primaryColor))];
  }

  private getLinearGradientOpts(fillHeight: number): Fabric.GradientOptions<'linear'> {
    if (this.fillOpts.getLinearGradientOpts) {
      return this.fillOpts.getLinearGradientOpts(fillHeight, this.fillColor);
    }

    return getLinearGradientOpts(fillHeight, this.fillColor);
  }

  private getRadialGradientOpts(fillWidth: number, fillHeight: number): Fabric.GradientOptions<'radial'> {
    if (this.fillOpts.getRadialGradientOpts) {
      return this.fillOpts.getRadialGradientOpts(fillWidth, fillHeight, this.fillColor);
    }

    return getRadialGradientOpts(fillWidth, fillHeight, this.fillColor);
  }
}

export const getLinearGradientOpts = (fillHeight: number, gradientFillColors: RGBA[]): Fabric.GradientOptions<'linear'> => {
  const x = 0;
  const y = fillHeight * 0.75;

  const colorStops = [];
  for (let i = 0; i < gradientFillColors.length; i++) {
    colorStops.push({
      offset: (i + 1) / gradientFillColors.length,
      color: rgbaToRGBAString(gradientFillColors[i]),
    });
  }

  return {
    colorStops,
    coords: {
      x1: x,
      y1: -y,
      x2: x,
      y2: y,
    },
    type: 'linear',
  };
};

export const getRadialGradientOpts = (fillWidth: number, fillHeight: number, gradientFillColors: RGBA[]): Fabric.GradientOptions<'radial'> => {
  const x = fillWidth;
  const y = fillHeight;
  const midX = fillWidth / 2;
  const midY = fillHeight / 2;
  const maxDimension = Math.max(x, y);

  const colorStops = [];
  for (let i = 0; i < gradientFillColors.length; i++) {
    colorStops.push({
      offset: Math.round(i / gradientFillColors.length),
      color: rgbaToRGBAString(gradientFillColors[i]),
    });
  }

  return {
    colorStops,
    coords: {
      r1: 0.1 * maxDimension,
      r2: maxDimension,
      x1: midX,
      y1: midY,
      x2: midX,
      y2: midY,
    },
    type: 'radial',
  };
};
